import {
  Box,
  Button,
  MenuItem,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import { CasLoader } from "../../brand/CasLoader";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  useComponent,
  useFetchComponentListener,
} from "../../../hook/component";
import { useAppIdMap } from "../../../hook/app";
import { ComponentBlock } from "../../../types/notebook/note/block/component";
import { CopyClipboard } from "../../copyClipboard";
import { DynamicTextField } from "../../dynamicTextField";
import { NoteBlockContainer } from "../../note";
import {
  getPackage,
  writeComponentPackage,
  deleteComponentPackage,
} from "../../../api/package";
import { Package } from "../../../types/package";
import { insertPageBlock } from "../../../api/page";
import { usePackageSearch, useAddPackages } from "../../../hook/package";
import { RootState } from "../../../reducer";

const PackagePicker: React.FC<{
  componentId: string;
  currentPackageId?: string;
  onPackageChange?: (packageId: string | undefined) => void;
}> = ({ componentId, currentPackageId, onPackageChange }) => {
  const { appId } = useAppIdMap();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedPackageId, setSelectedPackageId] = useState<
    string | undefined
  >(currentPackageId);

  const open = Boolean(anchorEl);

  const packagesMap = useSelector((state: RootState) => state.ui.packages);
  const addPackages = useAddPackages();

  useEffect(() => {
    if (!selectedPackageId) return;
    if (packagesMap[selectedPackageId]) return;
    getPackage({ "package-id": selectedPackageId }).then((pkg) => {
      if (pkg) addPackages([pkg]);
    });
  }, [selectedPackageId]);

  const { page: results, searchString, setSearchString, isLoading } = usePackageSearch({ disabledSearch: !open });

  const handleSelect = async (pkg: Package) => {
    if (!appId) return;
    await writeComponentPackage({
      "app-id": appId,
      "component-id": componentId,
      "package-id": pkg["package-id"],
    });
    setSelectedPackageId(pkg["package-id"]);
    onPackageChange?.(pkg["package-id"]);
    setAnchorEl(null);
  };

  const handleClear = async () => {
    if (!selectedPackageId) return;
    await deleteComponentPackage({
      "component-id": componentId,
      "package-id": selectedPackageId,
    });
    setSelectedPackageId(undefined);
    onPackageChange?.(undefined);
  };

  const selectedPkg = packagesMap[selectedPackageId ?? ""] ?? results.find((p) => p["package-id"] === selectedPackageId);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
      <Button
        variant="outlined"
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        {selectedPkg?.name ?? (selectedPackageId ? `Package: ${selectedPackageId}` : "Assign Package")}
      </Button>
      {selectedPackageId && (
        <Button size="small" color="warning" onClick={handleClear}>
          Clear
        </Button>
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, width: "20rem" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6">Select Package</Typography>
            {isLoading && <CasLoader size={20} />}
          </Box>
          <TextField
            fullWidth
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
            placeholder="Search packages..."
            sx={{ mt: 2 }}
          />
          <Box sx={{ height: "10rem", overflowY: "auto", mt: 1 }}>
            {results.map((pkg) => (
              <MenuItem key={pkg["package-id"]} onClick={() => handleSelect(pkg)}>
                {pkg.name}
              </MenuItem>
            ))}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export const ComponentBlockComponent: React.FC<{
  block: ComponentBlock;
}> = React.memo(({ block }) => {
  const { component, setComponentName } = useComponent(block["component-id"]);
  useFetchComponentListener(block["component-id"]);
  const componentName = component?.name ?? "";

  const handlePackageChange = (packageId: string | undefined) => {
    const updatedBlock: ComponentBlock = { ...block, "package-id": packageId };
    insertPageBlock({ block: updatedBlock, "block-index": block["block-index"] ?? 0 });
  };

  return (
    <NoteBlockContainer name="Component">
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography>{block["component-id"]}</Typography>
        <CopyClipboard value={block["component-id"]} />
      </Box>
      <DynamicTextField text={componentName} setText={setComponentName} />
      <PackagePicker
        componentId={block["component-id"]}
        currentPackageId={block["package-id"]}
        onPackageChange={handlePackageChange}
      />
    </NoteBlockContainer>
  );
});
